import React from "react";
import { motion } from "framer-motion";

const FloatingElement = ({ delay = 0, size = 100, left = "50%" }) => (
  <motion.div
    className="absolute opacity-0"
    style={{
      left,
      width: size,
      height: size,
    }}
    initial={{ y: "100%", opacity: 0 }}
    animate={{
      y: ["-20%", "120%"],
      opacity: [0, 0.15, 0],
      rotate: [0, 360],
    }}
    transition={{
      duration: 15,
      delay,
      repeat: Infinity,
      ease: "linear",
    }}
  >
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <path
        d="M50 0 L60 40 L100 50 L60 60 L50 100 L40 60 L0 50 L40 40 Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
        className="text-gold/30"
      />
    </svg>
  </motion.div>
);

export default function About() {
  return (
    <section id="about" className="relative py-24 overflow-hidden">
      {/* Premium Gradient Background */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5 }}
        className="absolute inset-0 bg-gradient-to-b from-purple900 via-purple800 to-purple600"
      />

      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Vertical Lines */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={`line-${i}`}
            className="absolute h-full w-px bg-gradient-to-b from-transparent via-gold/10 to-transparent"
            style={{ left: `${12.5 * (i + 1)}%` }}
            initial={{ y: "-100%" }}
            animate={{ y: "100%" }}
            transition={{
              duration: 20 + i * 2,
              repeat: Infinity,
              ease: "linear",
              delay: i * -3,
            }}
          />
        ))}

        {/* Floating Elements */}
        {[...Array(6)].map((_, i) => (
          <FloatingElement
            key={`float-${i}`}
            delay={i * 2}
            size={80 + Math.random() * 60}
            left={`${15 + (i * 15)}%`}
          />
        ))}

        {/* Decorative Circles */}
        <div className="absolute inset-0">
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={`circle-${i}`}
              className="absolute rounded-full border border-gold/10"
              style={{
                width: `${300 + i * 100}px`,
                height: `${300 + i * 100}px`,
                left: "50%",
                top: "50%",
                marginLeft: `-${150 + i * 50}px`,
                marginTop: `-${150 + i * 50}px`,
              }}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{
                scale: [0.8, 1.2, 0.8],
                opacity: [0.1, 0.3, 0.1],
                rotate: [0, 360],
              }}
              transition={{
                duration: 20 + i * 5,
                repeat: Infinity,
                ease: "linear",
              }}
            />
          ))}
        </div>
      </div>

      <div className="relative container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-radon text-gold mb-6 font-medium">
            Our Story
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-gold to-secondary mx-auto rounded-full" />
        </motion.div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto">
          {/* Introduction */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16 relative"
          >
            <div className="absolute -left-8 top-0 w-4 h-4 rounded-full bg-gold/20" />
            <div className="absolute -right-8 bottom-0 w-4 h-4 rounded-full bg-secondary/20" />
            <p className="text-xl md:text-2xl text-gold/90 font-konnect leading-relaxed italic">
              At KesariGlow, we don't just create skincare — we create moments of self-care inspired by heritage, elevated by science, and powered by purpose.
            </p>
          </motion.div>

          {/* Story Sections */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8 mb-16"
          >
            <div className="bg-purple900/30 backdrop-blur-sm rounded-2xl p-8 border border-gold/10">
              <p className="text-purple100/90 font-konnect leading-relaxed">
                Our story begins with a simple yet powerful belief: true beauty glows from within, and skincare should honor both tradition and innovation. Drawing from the timeless richness of Indian rituals and the golden touch of saffron (Kesari), we've built a brand that's deeply rooted in authenticity.
              </p>
            </div>
            
            <div className="text-center py-8">
              <p className="text-xl md:text-2xl font-medium text-secondary/90 font-radon">
                But we're not here to follow trends — we're here to set a new standard.
              </p>
            </div>

            <div className="bg-purple900/30 backdrop-blur-sm rounded-2xl p-8 border border-gold/10">
              <p className="text-purple100/90 font-konnect leading-relaxed">
                We're on a mission to make skincare that's not only effective, but meaningful. Thoughtfully formulated. Ethically created. Beautifully delivered. Our vision is to become a global voice for conscious beauty — one that speaks to the modern world while staying grounded in culture and care.
              </p>
            </div>
          </motion.div>

          {/* Values */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="grid md:grid-cols-3 gap-6 mb-16"
          >
            {[
              "For our customers, that means results you can trust.",
              "For our partners, that means quality you can stand behind.",
              "And for all of us — it means building something that lasts."
            ].map((text, index) => (
              <div 
                key={index}
                className="bg-purple900/20 backdrop-blur-sm rounded-xl p-6 border border-gold/10
                  hover:border-gold/30 transition-all duration-300"
              >
                <p className="text-purple100/90 font-konnect text-center">{text}</p>
              </div>
            ))}
          </motion.div>

          {/* Closing Statement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-center space-y-3 pt-8 relative"
          >
            <div className="absolute left-1/2 -top-8 w-px h-16 bg-gradient-to-b from-transparent via-gold/20 to-transparent" />
            <p className="text-2xl md:text-3xl text-gold font-radon mb-6">KesariGlow is more than a name.</p>
            <div className="space-y-2 text-lg text-secondary/90 font-radon">
              <p>It's a promise.</p>
              <p>A journey.</p>
              <p>And a celebration of skin that shines with pride.</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
} 